/**
 * 条件规则：前后端格式转换、摘要文案与快捷模板
 *
 * 为什么单独成文件：条件表单现在有两个入口 —— 条件管理页（condition-edit.vue）
 * 与推送任务页内的新建/编辑对话框（ConditionFormDialog.vue）。转换逻辑（操作符映射、
 * 多值归一、提交前过滤）必须只有一份，否则两处会慢慢跑偏（历史上「列表页与编辑页
 * 两份硬编码不同步」已经踩过一次）。纯函数也便于 jest 直接测。
 */
import { enumValueLabelKey, fieldLabelKey, isEnumField, operatorLabelKey } from './condition-fields'

/** 前端操作符 => 后端标准操作符（库里与 compareValues 只认这一套） */
export const FRONT_TO_BACK_OPERATOR = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  contains: 'IN',
  notContains: 'NOT IN'
}

/** 后端标准操作符 => 前端下拉值（回显用） */
export const BACK_TO_FRONT_OPERATOR = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  IN: 'contains',
  'NOT IN': 'notContains'
}

/**
 * 「包含 / 不包含」对应后端 IN / NOT IN，值支持多选（存逗号分隔字符串）
 */
export function isMultiValueOperator(operator) {
  return operator === 'contains' || operator === 'notContains'
}

/**
 * 回显归一：枚举字段 + 多值操作符 → 数组（喂给多选下拉）；
 * 其余情况统一成标量，避免历史数据里的逗号串或数组把控件显示搞乱
 */
export function normalizeRuleValueForForm(field, operator, rawValue) {
  if (isEnumField(field) && isMultiValueOperator(operator)) {
    if (Array.isArray(rawValue)) return rawValue.slice()
    return splitList(rawValue)
  }
  if (Array.isArray(rawValue)) {
    return rawValue.length ? String(rawValue[0]) : ''
  }
  return rawValue === null || rawValue === undefined ? '' : rawValue
}

/** 逗号分隔串 → 去空去白的数组（多选反显用） */
function splitList(rawValue) {
  return String(rawValue === null || rawValue === undefined ? '' : rawValue)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

/**
 * 整条规则的前端回显归一（后端规则 → 表单规则）
 */
export function normalizeRuleForForm(rule) {
  const field = (rule && rule.field) || ''
  const operator = BACK_TO_FRONT_OPERATOR[(rule && rule.operator) || ''] || (rule && rule.operator) || ''
  return {
    field,
    operator,
    value: normalizeRuleValueForForm(field, operator, rule ? rule.value : '')
  }
}

/** 规则是否填写完整（多选下拉的值是数组，空数组视为未填） */
export function isRuleComplete(rule) {
  if (!rule || !rule.field || !rule.operator) return false
  if (Array.isArray(rule.value)) return rule.value.length > 0
  return rule.value !== ''
}

/**
 * 提交前：丢掉没填完的规则 + 操作符转标准形式 + 多选数组 join 成逗号串
 * （与后端 compareValues 对 IN / NOT IN 的解析方式一致，库里存的始终是字符串）
 */
export function buildSubmitRules(rules) {
  return (rules || []).filter(isRuleComplete).map(rule => ({
    field: rule.field,
    operator: FRONT_TO_BACK_OPERATOR[rule.operator] || rule.operator,
    value: Array.isArray(rule.value) ? rule.value.join(',') : rule.value
  }))
}

/** 条件的匹配模式（前端 radio 值）转后端 match_all */
export function matchAllOf(matchMode) {
  return matchMode === 'OR' ? 0 : 1
}

/**
 * 单条规则的展示文案，如「本周录音数 = 0」「性别 包含 女 / 男」
 *
 * @param {object} rule 后端格式的规则（operator 为标准操作符或前端值均可）
 * @param {function} t i18n 取值函数，key 不含 autoPush. 前缀
 */
export function summarizeRule(rule, t) {
  if (!rule) return ''
  const fieldKey = fieldLabelKey(rule.field)
  const field = fieldKey ? t(fieldKey) : rule.field
  const opKey = operatorLabelKey(BACK_TO_FRONT_OPERATOR[rule.operator] || rule.operator)
  const operator = opKey ? t(opKey) : rule.operator
  const value = summarizeValue(rule.field, rule.value, t)
  return `${field} ${operator} ${value}`
}

/** 规则值的展示：枚举值翻成本地化标签，多值用「 / 」连接 */
export function summarizeValue(field, value, t) {
  if (value === null || value === undefined || value === '') return '-'
  const parts = Array.isArray(value) ? value : String(value).split(',')
  return parts
    .map(part => {
      const labelKey = enumValueLabelKey(field, String(part).trim())
      return labelKey ? t(labelKey) : String(part).trim()
    })
    .filter(Boolean)
    .join(' / ')
}

/**
 * 整个条件的摘要（用于任务页下拉项）：规则用「且 / 或」连接
 *
 * 规则多于 2 条时只显示前 2 条加省略号 —— 下拉项只是让人一眼认出是哪个条件，
 * 完整规则到编辑对话框里看。
 *
 * @param {object} condition getConditions 返回的条件对象（含 rules / matchMode）
 * @param {function} t i18n 取值函数
 */
export function summarizeCondition(condition, t) {
  const rules = (condition && condition.rules) || []
  if (!rules.length) return ''
  const joiner = condition.matchMode === 'OR' ? ` ${t('condSummaryOr')} ` : ` ${t('condSummaryAnd')} `
  const shown = rules.slice(0, 2).map(rule => summarizeRule(rule, t))
  if (rules.length > 2) shown.push('…')
  return shown.join(joiner)
}

/**
 * 新建条件时的常用模板（一键预填规则）
 *
 * 覆盖运营最常用的几条：VJ 打卡、未读提醒、连续未读。字段与取值必须与后端
 * AutoPushService::getUserFieldValue 支持的一致。
 */
export const CONDITION_QUICK_TEMPLATES = [
  {
    key: 'vjThisWeekZero',
    labelKey: 'condTplVjThisWeekZero',
    nameKey: 'condTplVjThisWeekZeroName',
    matchMode: 'AND',
    rules: [{ field: 'vjCountThisWeek', operator: 'eq', value: '0' }]
  },
  {
    // 「≥3 天没录」口径，含从未录过的人（vjDaysSinceLastOrNever 把从未录音当 999 天）。
    // 这是运营最常用的配法：一条规则 + 全部满足即可，不必依赖「任一满足」
    key: 'vjDaysOrNever3',
    labelKey: 'condTplVjDaysOrNever3',
    nameKey: 'condTplVjDaysOrNever3Name',
    matchMode: 'AND',
    rules: [{ field: 'vjDaysSinceLastOrNever', operator: 'gte', value: '3' }]
  },
  {
    key: 'unread3',
    labelKey: 'condTplUnread3',
    nameKey: 'condTplUnread3Name',
    matchMode: 'AND',
    rules: [{ field: 'unreadCount', operator: 'gte', value: '3' }]
  },
  {
    key: 'unread7d1',
    labelKey: 'condTplUnread7d1',
    nameKey: 'condTplUnread7d1Name',
    matchMode: 'AND',
    rules: [{ field: 'unreadCount7d', operator: 'gte', value: '1' }]
  },
  {
    key: 'consecutiveUnread3',
    labelKey: 'condTplConsecutiveUnread3',
    nameKey: 'condTplConsecutiveUnread3Name',
    matchMode: 'AND',
    rules: [{ field: 'consecutiveUnreadCount', operator: 'gte', value: '3' }]
  }
]
